import { useCallback, useEffect, useRef } from 'react'
import { useLogin, usePrivy } from '@privy-io/react-auth'

type UseLoginParams = Parameters<typeof useLogin>[0]

/**
 * Ready-guarded wrapper around Privy's `useLogin`.
 *
 * Privy's `login()` silently no-ops when it is called before the SDK has
 * finished initializing (`usePrivy().ready === false`). On mobile the SDK
 * initializes noticeably slower than on desktop, so a user who taps
 * "Login" / "Sign up" right away lands inside that window and the login
 * modal never opens — which looks like "login is broken on mobile".
 *
 * This hook fixes that by remembering the tap when Privy isn't ready yet and
 * firing `login()` automatically the instant it becomes ready. When Privy is
 * already ready (the common desktop case) it behaves exactly like a direct
 * `login()` call.
 *
 * Options (`onComplete` / `onError`) are passed straight through to Privy's
 * `useLogin`, so callers keep full access to the completion params.
 */
export function useAppLogin(options?: UseLoginParams) {
  const { ready } = usePrivy()
  const { login: privyLogin } = useLogin(options)

  // True when the user requested login while Privy was still initializing.
  const pendingRef = useRef(false)

  useEffect(() => {
    if (pendingRef.current && ready) {
      pendingRef.current = false
      privyLogin()
    }
  }, [ready, privyLogin])

  const login = useCallback(() => {
    if (!ready) {
      // Privy not initialized yet — remember the intent and open the modal
      // as soon as `ready` flips true (see effect above).
      pendingRef.current = true
      return
    }
    privyLogin()
  }, [ready, privyLogin])

  return { login, ready }
}

export default useAppLogin
